import { useEffect, useMemo, useRef, useState } from 'react';

import { handleApiError } from 'api/error-handling';
import { MovementWithSection } from 'components/Leaderboard/movementLabel';
import {
  MovementResult,
  ResultsState,
  WorkoutResultWithSets,
  buildInitialLevels,
  buildInitialResults,
  buildResultPayload,
  enrichSections,
  getDefaultLevel,
  isMovementResultFilled,
  sectionHasLevels,
  sectionHasServerResult,
} from 'components/Workout/logging';
import useClass from 'hooks/class/useClass';
import useWorkoutAthleteResults from 'hooks/workoutAthleteResult/useWorkoutAthleteResults';
import useLogWorkoutResultAsStaff from 'hooks/workoutResult/useLogWorkoutResultAsStaff';
import useSetSectionLevelAsStaff from 'hooks/workoutResult/useSetSectionLevelAsStaff';
import type { WorkoutRetrieve } from 'types';
import { WorkoutLevelEnum } from 'types/enums';
import type { AthleteKiloClassWithAthleteAndWaitlistOrder } from 'types/generated/models/AthleteKiloClassWithAthleteAndWaitlistOrder';
import notificationHandler from 'utils/notifications';

export type WhiteboardResults = Record<string, ResultsState>;
export type WhiteboardLevels = Record<string, Record<string, WorkoutLevelEnum | null>>;

interface UseWhiteboardResultsValue {
  athletes: AthleteKiloClassWithAthleteAndWaitlistOrder[];
  movements: MovementWithSection[];
  results: WhiteboardResults;
  levels: WhiteboardLevels;
  isClassLoading: boolean;
  location: string | null;
  coachNames: string;
  handleCellChange: (athleteKiloClassId: string, movementId: string, value: MovementResult) => void;
  handleCellCommit: (
    athleteId: string,
    athleteKiloClassId: string,
    movementId: string,
    value: MovementResult,
  ) => Promise<void>;
  handleLevelChange: (athleteKiloClassId: string, workoutSectionId: string, level: WorkoutLevelEnum) => void;
  handleLevelCommit: (
    athleteId: string,
    athleteKiloClassId: string,
    workoutSectionId: string,
    level: WorkoutLevelEnum,
  ) => Promise<void>;
}

const isEqual = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

// Preserves cells the user is currently editing (local !== last server snapshot);
// every other cell takes the latest server value.
const mergeResults = (
  athletes: AthleteKiloClassWithAthleteAndWaitlistOrder[],
  current: WhiteboardResults,
  prevServer: WhiteboardResults,
  newServer: WhiteboardResults,
): { merged: WhiteboardResults; hasExternalUpdate: boolean; hasChanges: boolean } => {
  const merged: WhiteboardResults = {};
  let hasExternalUpdate = false;
  let hasChanges = false;

  athletes.forEach(athlete => {
    const akcId = athlete.athleteKiloClassId;
    const currentRow = current[akcId] ?? {};
    const prevServerRow = prevServer[akcId] ?? {};
    const newServerRow = newServer[akcId] ?? {};
    merged[akcId] = {};

    const movementIds = new Set([...Object.keys(currentRow), ...Object.keys(newServerRow)]);
    movementIds.forEach(movId => {
      const localVal = currentRow[movId];
      const prevServerVal = prevServerRow[movId];
      const hasLocalEdit =
        localVal !== undefined && prevServerVal !== undefined && !isEqual(localVal, prevServerVal);
      const nextVal = hasLocalEdit ? localVal : newServerRow[movId] ?? localVal;

      merged[akcId][movId] = nextVal;
      if (!isEqual(nextVal, localVal)) hasChanges = true;
      if (!hasLocalEdit && !isEqual(newServerRow[movId] ?? localVal, localVal)) {
        hasExternalUpdate = true;
      }
    });
  });

  return { merged, hasExternalUpdate, hasChanges };
};

// Levels commit immediately on tap (no lingering mid-edit draft like a typed score),
// but this still guards a poll landing in the narrow window between a tap and its
// commit resolving — same "preserve the local edit" shape as mergeResults, kept
// separate rather than genericized since the two states commit very differently.
const mergeLevels = (
  athletes: AthleteKiloClassWithAthleteAndWaitlistOrder[],
  current: WhiteboardLevels,
  prevServer: WhiteboardLevels,
  newServer: WhiteboardLevels,
): { merged: WhiteboardLevels; hasChanges: boolean } => {
  const merged: WhiteboardLevels = {};
  let hasChanges = false;

  athletes.forEach(athlete => {
    const akcId = athlete.athleteKiloClassId;
    const currentRow = current[akcId] ?? {};
    const prevServerRow = prevServer[akcId] ?? {};
    const newServerRow = newServer[akcId] ?? {};
    merged[akcId] = {};

    const sectionIds = new Set([...Object.keys(currentRow), ...Object.keys(newServerRow)]);
    sectionIds.forEach(sectionId => {
      const localVal = currentRow[sectionId];
      const prevServerVal = prevServerRow[sectionId];
      const hasLocalEdit =
        localVal !== undefined && prevServerVal !== undefined && localVal !== prevServerVal;
      const nextVal = hasLocalEdit ? localVal : newServerRow[sectionId] ?? localVal;
      merged[akcId][sectionId] = nextVal;
      if (nextVal !== localVal) hasChanges = true;
    });
  });

  return { merged, hasChanges };
};

const useWhiteboardResults = (workout: WorkoutRetrieve, classId: string): UseWhiteboardResultsValue => {
  const { errorNotification, infoNotification } = useMemo(() => notificationHandler(), []);

  const { data: classData, isLoading: isClassLoading } = useClass(classId);
  const { data: resultsData, isLoading: isResultsLoading } = useWorkoutAthleteResults(workout.workoutId);
  const logResult = useLogWorkoutResultAsStaff(workout.workoutId);
  const setSectionLevelAsStaff = useSetSectionLevelAsStaff(workout.workoutId);

  const athletes = useMemo<AthleteKiloClassWithAthleteAndWaitlistOrder[]>(
    () =>
      (classData?.data?.athleteKiloClasses ?? []).filter(
        a => (a.isReserved || a.isCheckedIn) && !a.isLateCancel && !a.isNoShow && !a.isWaitlisted,
      ),
    [classData],
  );

  const enrichedSections = useMemo(() => enrichSections(workout.sections), [workout.sections]);

  const movements = useMemo<MovementWithSection[]>(
    () =>
      [...enrichedSections]
        .sort((a, b) => a.orderIndex - b.orderIndex)
        .flatMap(section =>
          [...section.movements]
            .sort((a, b) => a.orderIndex - b.orderIndex)
            .map(m => ({
              ...m,
              sectionName: section.name,
              workoutSectionId: section.workoutSectionId,
              levelScaled: section.levelScaled,
              levelRx: section.levelRx,
              levelRxPlus: section.levelRxPlus,
            })),
        ),
    [enrichedSections],
  );

  // Results are keyed by athleteId in the API but by athleteKiloClassId in local state,
  // because a class roster has one row per AthleteKiloClass, not per Athlete.
  const apiResultsByAthleteId = useMemo(() => {
    const list = resultsData?.data ?? [];
    return new Map(list.map(a => [a.athleteId, (a.results ?? []) as unknown as WorkoutResultWithSets[]]));
  }, [resultsData]);

  const serverResults = useMemo<WhiteboardResults>(() => {
    const state: WhiteboardResults = {};
    athletes.forEach(athlete => {
      const apiResults = apiResultsByAthleteId.get(athlete.athlete.athleteId) ?? [];
      state[athlete.athleteKiloClassId] = buildInitialResults(enrichedSections, apiResults);
    });
    return state;
  }, [athletes, apiResultsByAthleteId, enrichedSections]);

  const serverLevels = useMemo<WhiteboardLevels>(() => {
    const state: WhiteboardLevels = {};
    athletes.forEach(athlete => {
      const apiResults = apiResultsByAthleteId.get(athlete.athlete.athleteId) ?? [];
      state[athlete.athleteKiloClassId] = buildInitialLevels(enrichedSections, apiResults);
    });
    return state;
  }, [athletes, apiResultsByAthleteId, enrichedSections]);

  const coachNames = useMemo(() => {
    const roles = classData?.data?.kiloClassCoachKiloClassCoachRoles ?? [];
    return roles.map(r => r.coach.fullName).join(', ');
  }, [classData]);

  const location = classData?.data?.location?.name ?? null;

  const [results, setResults] = useState<WhiteboardResults>({});
  const [levels, setLevels] = useState<WhiteboardLevels>({});
  const hasHydratedRef = useRef(false);
  const prevServerSnapshotRef = useRef<WhiteboardResults>({});
  const prevServerLevelsRef = useRef<WhiteboardLevels>({});
  // Mirrors `results` so the merge effect can read the latest local state without
  // re-running on every keystroke.
  const resultsRef = useRef<WhiteboardResults>({});
  useEffect(() => {
    resultsRef.current = results;
  }, [results]);
  const levelsRef = useRef<WhiteboardLevels>({});
  useEffect(() => {
    levelsRef.current = levels;
  }, [levels]);

  // First-load hydration: copy server results into local state once both queries resolve.
  useEffect(() => {
    if (hasHydratedRef.current) return;
    if (isResultsLoading || isClassLoading) return;
    if (athletes.length === 0) return;
    setResults(serverResults);
    resultsRef.current = serverResults;
    prevServerSnapshotRef.current = serverResults;
    setLevels(serverLevels);
    levelsRef.current = serverLevels;
    prevServerLevelsRef.current = serverLevels;
    hasHydratedRef.current = true;
  }, [serverResults, serverLevels, isResultsLoading, isClassLoading, athletes.length]);

  // Each poll merges the server snapshot into local state. Own-save refetches don't
  // surface a toast because the saved value already matches local state by then.
  useEffect(() => {
    if (!hasHydratedRef.current) return;

    const { merged, hasExternalUpdate, hasChanges } = mergeResults(
      athletes,
      resultsRef.current,
      prevServerSnapshotRef.current,
      serverResults,
    );

    prevServerSnapshotRef.current = serverResults;

    if (hasChanges) setResults(merged);
    if (hasExternalUpdate) infoNotification('New results synced from server');
  }, [serverResults, athletes, infoNotification]);

  useEffect(() => {
    if (!hasHydratedRef.current) return;

    const { merged, hasChanges } = mergeLevels(
      athletes,
      levelsRef.current,
      prevServerLevelsRef.current,
      serverLevels,
    );
    prevServerLevelsRef.current = serverLevels;
    if (hasChanges) setLevels(merged);
  }, [serverLevels, athletes]);

  const handleCellChange = (athleteKiloClassId: string, movementId: string, value: MovementResult): void => {
    setResults(prev => ({
      ...prev,
      [athleteKiloClassId]: { ...prev[athleteKiloClassId], [movementId]: value },
    }));
  };

  const handleCellCommit = async (
    athleteId: string,
    athleteKiloClassId: string,
    movementId: string,
    value: MovementResult,
  ): Promise<void> => {
    if (!isMovementResultFilled(value)) throw new Error('Result is empty; not committing.');
    const section = enrichedSections.find(s =>
      s.movements.some(m => m.workoutSectionMovementId === movementId),
    );
    const level =
      section && sectionHasLevels(section)
        ? levels[athleteKiloClassId]?.[section.workoutSectionId] ?? getDefaultLevel(section)
        : null;
    try {
      await logResult.mutateAsync({
        ...buildResultPayload(movementId, value, athleteKiloClassId),
        athlete: athleteId,
        level,
      });
    } catch (err) {
      errorNotification("Couldn't save result", handleApiError(err).message);
      throw err;
    }
  };

  const handleLevelChange = (
    athleteKiloClassId: string,
    workoutSectionId: string,
    level: WorkoutLevelEnum,
  ): void => {
    setLevels(prev => ({
      ...prev,
      [athleteKiloClassId]: { ...prev[athleteKiloClassId], [workoutSectionId]: level },
    }));
  };

  const handleLevelCommit = async (
    athleteId: string,
    _athleteKiloClassId: string,
    workoutSectionId: string,
    level: WorkoutLevelEnum,
  ): Promise<void> => {
    const section = enrichedSections.find(s => s.workoutSectionId === workoutSectionId);
    const apiResults = apiResultsByAthleteId.get(athleteId) ?? [];
    // No result logged yet for this section — nothing to bulk-update server-side;
    // the picked level rides along on the section's first commit instead.
    if (!section || !sectionHasServerResult(section, apiResults)) return;
    try {
      await setSectionLevelAsStaff.mutateAsync({
        athlete: athleteId,
        workoutSection: workoutSectionId,
        level,
      });
    } catch (err) {
      errorNotification("Couldn't save level", handleApiError(err).message);
      throw err;
    }
  };

  return {
    athletes,
    movements,
    results,
    levels,
    isClassLoading,
    location,
    coachNames,
    handleCellChange,
    handleCellCommit,
    handleLevelChange,
    handleLevelCommit,
  };
};

export default useWhiteboardResults;
